import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Receipt, FileText, Plus, ArrowRight } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { getExpenses, getInvoices } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'

export default function StaffDashboard() {
  const { user } = useAuth()
  const [recentExpenses, setRecentExpenses] = useState([])
  const [recentInvoices, setRecentInvoices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      getExpenses({ take: 5 }).catch(() => ({ data: { expenses: [] } })),
      getInvoices({ take: 5 }).catch(() => ({ data: { invoices: [] } })),
    ]).then(([expRes, invRes]) => {
      setRecentExpenses(expRes.data.expenses || [])
      setRecentInvoices(invRes.data.invoices || [])
    }).finally(() => setLoading(false))
  }, [])

  const pendingExpenses = recentExpenses.filter((e) => e.status === 'SUBMITTED')
  const totalPending = pendingExpenses.reduce((sum, e) => sum + Number(e.amount), 0)

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Welcome, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-gray-500 mt-1">Here's a quick overview of your activity.</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/expenses/new"
          className="card flex items-center gap-4 hover:border-brand-300 hover:shadow-md transition-all group cursor-pointer"
        >
          <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 transition-colors">
            <Receipt className="h-6 w-6" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Submit Expense</p>
            <p className="text-xs text-gray-500">Claim a reimbursement</p>
          </div>
          <Plus className="h-5 w-5 text-gray-300 ml-auto group-hover:text-brand-500 transition-colors" />
        </Link>
        <Link
          to="/invoices/new"
          className="card flex items-center gap-4 hover:border-brand-300 hover:shadow-md transition-all group cursor-pointer"
        >
          <div className="p-3 rounded-lg bg-brand-50 text-brand-600 group-hover:bg-brand-100 transition-colors">
            <FileText className="h-6 w-6" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Create Invoice</p>
            <p className="text-xs text-gray-500">Draft for admin approval</p>
          </div>
          <Plus className="h-5 w-5 text-gray-300 ml-auto group-hover:text-brand-500 transition-colors" />
        </Link>
      </div>

      {/* Pending summary */}
      {pendingExpenses.length > 0 && (
        <div className="card border-l-4 border-l-amber-400 !py-4">
          <p className="text-sm text-gray-700">
            You have <span className="font-semibold">{pendingExpenses.length}</span> expense{pendingExpenses.length > 1 ? 's' : ''} pending approval
            totalling <span className="font-semibold">{formatCurrency(totalPending)}</span>
          </p>
        </div>
      )}

      {/* Recent Expenses */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Recent Expenses</h2>
          <Link to="/expenses" className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {loading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : recentExpenses.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No expenses submitted yet.</p>
        ) : (
          <div className="space-y-2">
            {recentExpenses.map((e) => (
              <Link
                key={e.id}
                to={`/expenses/${e.id}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 line-clamp-1">{e.description}</p>
                  <p className="text-xs text-gray-400">{formatDate(e.date)} · {e.category?.name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{formatCurrency(e.amount)}</span>
                  <StatusBadge status={e.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent Invoices */}
      {recentInvoices.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">My Invoices</h2>
            <Link to="/invoices" className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="space-y-2">
            {recentInvoices.map((inv) => (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{inv.number || 'DRAFT'}</p>
                  <p className="text-xs text-gray-400">{inv.customer?.name} · {formatDate(inv.date)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{formatCurrency(inv.total, inv.currency)}</span>
                  <StatusBadge status={inv.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
